import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { useDataValidation } from './useDataValidation';

/**
 * Composable for baking group arrays and symmetry into individual objects
 */
export const useBakeGroup = () => {
  const cruxModuleStore = useCruxModuleStore();
  const selectionStore = useSelectionStore();
  const { sanitizeModuleData } = useDataValidation();

  /**
   * Calculate the transformed position for symmetry mode
   */
  const updateSymmetryVector3 = (vector3, properties) => {
    const plane = properties.symmetryPlane || 'yz';
    const offset = properties.symmetryOffset || 0;

    const result = { ...vector3 };

    switch (plane) {
      case 'xy':
        result.z = -vector3.z + offset * 2;
        break;
      case 'xz':
        result.y = -vector3.y + offset * 2;
        break;
      case 'yz':
        result.x = -vector3.x + offset * 2;
        break;
    }

    return result;
  };

  /**
   * Calculate the transformed rotation for symmetry mode
   */
  const updateSymmetryRotation = (rotation, properties) => {
    const plane = properties.symmetryPlane || 'yz';
    const result = { ...rotation };

    switch (plane) {
      case 'xy':
        result.x = -rotation.x;
        result.y = -rotation.y;
        break;
      case 'xz':
        result.x = -rotation.x;
        result.z = -rotation.z;
        break;
      case 'yz':
        result.y = -rotation.y;
        result.z = -rotation.z;
        break;
    }

    return result;
  };

  /**
   * Calculate the transformed position for linear array mode
   */
  const updateLinearArrayVector3 = (vector3, properties, arrayIndex) => {
    const translation = properties.arrayTranslation || { x: 0, y: 0, z: 0 };

    return {
      x: vector3.x + translation.x * arrayIndex,
      y: vector3.y + translation.y * arrayIndex,
      z: vector3.z + translation.z * arrayIndex,
    };
  };

  /**
   * Calculate the transformed rotation for linear array mode
   */
  const updateLinearArrayRotation = (rotation, properties, arrayIndex) => {
    const arrayRotation = properties.arrayRotation || { x: 0, y: 0, z: 0 };

    return {
      x: rotation.x + arrayRotation.x * arrayIndex,
      y: rotation.y + arrayRotation.y * arrayIndex,
      z: rotation.z + arrayRotation.z * arrayIndex,
    };
  };

  /**
   * Calculate the transformed position for polar array mode
   */
  const updatePolarArrayVector3 = (vector3, properties, arrayIndex) => {
    const polarCount = properties.polarCount || 6;
    const polarFullOverride = properties.polarFullOverride || false;
    const polarAngle = properties.polarAngle || 90;
    const polarAxis = properties.polarAxis || 'y';

    const angleStep = polarFullOverride ? 360 / (polarCount + 1) : polarAngle / polarCount;
    const angleRad = (angleStep * arrayIndex * Math.PI) / 180;

    const cos = Math.cos(angleRad);
    const sin = Math.sin(angleRad);

    const { x, y, z } = vector3;
    const result = { x, y, z };

    switch (polarAxis) {
      case 'x':
        result.y = y * cos - z * sin;
        result.z = y * sin + z * cos;
        break;
      case 'y':
        result.x = x * cos + z * sin;
        result.z = -x * sin + z * cos;
        break;
      case 'z':
        result.x = x * cos - y * sin;
        result.y = x * sin + y * cos;
        break;
    }

    return result;
  };

  /**
   * Calculate the transformed rotation for polar array mode
   */
  const updatePolarArrayRotation = (rotation, properties, arrayIndex) => {
    const polarCount = properties.polarCount || 6;
    const polarFullOverride = properties.polarFullOverride || false;
    const polarAngle = properties.polarAngle || 90;
    const polarAxis = properties.polarAxis || 'y';

    const angleStep = polarFullOverride ? 360 / (polarCount + 1) : polarAngle / polarCount;
    const angleDeg = angleStep * arrayIndex;

    const result = { ...rotation };
    switch (polarAxis) {
      case 'x':
        result.x = (result.x || 0) + angleDeg;
        break;
      case 'y':
        result.y = (result.y || 0) + angleDeg;
        break;
      case 'z':
        result.z = (result.z || 0) + angleDeg;
        break;
    }

    return result;
  };

  /**
   * Deep clone an object, removing clone-specific properties
   */
  const cloneModuleData = (module) => {
    const cloned = JSON.parse(JSON.stringify(module));

    // Remove clone-specific properties
    delete cloned.code;
    delete cloned.clone;
    delete cloned.clonedCode;
    if (cloned.data) {
      delete cloned.data.clonedCode;
    }

    return cloned;
  };

  /**
   * Bake array/symmetry items into real permanent objects
   */
  const bakeGroupItems = async () => {
    // Get the currently selected group
    const groupNodeId = selectionStore.selectedObjectId;

    if (!groupNodeId) {
      console.warn('No object selected');
      return;
    }

    const groupModule = cruxModuleStore.cruxModules.find((m) => m.code === groupNodeId);

    if (!groupModule) {
      console.warn('Selected object not found in cruxModules');
      return;
    }

    if (groupModule.type !== 'group') {
      console.warn('Selected object is not a group');
      return;
    }

    const groupContext = groupModule.data;
    const groupMode = groupContext.groupMode;

    if (groupMode !== 'symmetry' && groupMode !== 'linear_array' && groupMode !== 'polar_array') {
      console.warn('Can only bake symmetry, linear_array, or polar_array groups');
      return;
    }

    // Get all children of this group
    const children = cruxModuleStore.getSceneTreeModules(groupNodeId);

    if (!children || children.length === 0) {
      console.warn('No children found to bake');
      return;
    }

    const newGroups = [];

    // Process based on group mode
    if (groupMode === 'symmetry') {
      // Create one duplicate group with mirrored children
      const newGroupData = cloneModuleData(groupModule);
      newGroupData.name = `${groupModule.name} (Mirrored)`;
      newGroupData.type = 'group';
      newGroupData.data.groupMode = 'normal';
      newGroupData.data.parentCode = groupModule.data.parentCode;

      // Store the new group first
      const storedGroup = await cruxModuleStore.store(newGroupData);
      const newGroupCode = storedGroup.code;

      // Create transformed children in the new group
      for (const child of children) {
        const clonedData = cloneModuleData(child);

        // Transform position
        if (child.data.position) {
          clonedData.data.position = updateSymmetryVector3(child.data.position, groupContext);
        }

        // Transform target (for projectors)
        if (child.data.target) {
          clonedData.data.target = updateSymmetryVector3(child.data.target, groupContext);
        }

        // Transform rotation
        if (child.data.rotation) {
          clonedData.data.rotation = updateSymmetryRotation(child.data.rotation, groupContext);
        }

        clonedData.data.parentCode = newGroupCode;
        clonedData.name = child.name;
        clonedData.type = child.type;

        await cruxModuleStore.store(clonedData);
      }

      newGroups.push(storedGroup);
    } else if (groupMode === 'linear_array') {
      // Create arrayCount-1 duplicate groups (original already exists)
      const arrayCount = groupContext.arrayCount || 3;

      for (let i = 1; i < arrayCount; i++) {
        // Create a duplicate group
        const newGroupData = cloneModuleData(groupModule);
        newGroupData.name = `${groupModule.name} (${i})`;
        newGroupData.type = 'group';

        // Ensure data object exists
        if (!newGroupData.data) {
          newGroupData.data = {};
        }

        newGroupData.data.groupMode = 'normal';
        newGroupData.data.parentCode = groupModule.data.parentCode;

        // Transform the group position itself (always apply array translation)
        const originalPosition = groupModule.data.position || {
          x: 0,
          y: 0,
          z: 0,
        };
        const newPosition = updateLinearArrayVector3(originalPosition, groupContext, i);
        newGroupData.data.position = { ...newPosition };

        // Transform the group rotation
        const originalRotation = groupModule.data.rotation || {
          x: 0,
          y: 0,
          z: 0,
        };
        const newRotation = updateLinearArrayRotation(originalRotation, groupContext, i);
        newGroupData.data.rotation = { ...newRotation };

        console.log(`Creating group ${i} at position:`, newGroupData.data.position);

        // Store the new group first
        const storedGroup = await cruxModuleStore.store(newGroupData);
        const newGroupCode = storedGroup.code;

        // Clone all children into the new group (without additional transformation)
        for (const child of children) {
          const clonedData = cloneModuleData(child);
          clonedData.data.parentCode = newGroupCode;
          clonedData.name = child.name;
          clonedData.type = child.type;

          await cruxModuleStore.store(clonedData);
        }

        newGroups.push(storedGroup);
      }
    } else if (groupMode === 'polar_array') {
      const polarCount = groupContext.polarCount || 6;

      for (let i = 1; i <= polarCount; i++) {
        const newGroupData = cloneModuleData(groupModule);
        newGroupData.name = `${groupModule.name} (${i})`;
        newGroupData.type = 'group';

        if (!newGroupData.data) {
          newGroupData.data = {};
        }

        newGroupData.data.groupMode = 'normal';
        newGroupData.data.parentCode = groupModule.data.parentCode;

        const storedGroup = await cruxModuleStore.store(newGroupData);
        const newGroupCode = storedGroup.code;

        for (const child of children) {
          const clonedData = cloneModuleData(child);

          if (child.data.position) {
            clonedData.data.position = updatePolarArrayVector3(child.data.position, groupContext, i);
          }

          if (child.data.target) {
            clonedData.data.target = updatePolarArrayVector3(child.data.target, groupContext, i);
          }

          if (child.data.rotation) {
            clonedData.data.rotation = updatePolarArrayRotation(child.data.rotation, groupContext, i);
          }

          clonedData.data.parentCode = newGroupCode;
          clonedData.name = child.name;
          clonedData.type = child.type;

          await cruxModuleStore.store(clonedData);
        }

        newGroups.push(storedGroup);
      }
    }

    // Update the original group back to normal mode
    if (groupModule) {
      const updatedGroupData = sanitizeModuleData({
        ...groupModule,
        data: {
          ...groupModule.data,
          groupMode: 'normal',
        },
      });
      await cruxModuleStore.update(groupNodeId, updatedGroupData);
    }

    console.log(`Baked ${newGroups.length} duplicate group(s) from ${groupMode} group`);
  };

  return {
    bakeGroupItems,
  };
};
